/**
 * Phase 15 Plan 15-02 — typed axios wrappers for the 6 `/api/logsheets/monthly-*` endpoints
 * defined in Plan 15-01.
 *
 * Endpoint mapping (actual backend routes from LogsheetsMonthlyController):
 *   GET  /api/logsheets/monthly-summary                        → getMonthlySummaryRequest
 *   GET  /api/logsheets/monthly-summary/entries/{machineId}    → getEntriesForMachineMonthRequest
 *   GET  /api/logsheets/monthly-summary/eligible               → getEligibleForMonthlyDocumentRequest
 *   POST /api/logsheets/monthly-documents                      → uploadMonthlyDocumentsRequest
 *   GET  /api/logsheets/monthly-summary/export.csv             → getMonthlySummaryCsvRequest
 *   GET  /api/logsheets/monthly-summary/export.pdf             → getMonthlySummaryPdfRequest
 *
 * Mirrors the Phase 14 logsheet-approver.ts pattern:
 *  - Uses the singleton `api` axios instance (JWT interceptor + Token-Expired handler).
 *  - Each function throws an `ApiError` (via `toApiError`) on non-2xx.
 *  - Query params passed via `params:` (axios serialises them).
 *  - Blob download uses responseType: 'blob' and returns a Blob.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  EligibleMonthlyTuple,
  MonthlyEntryItem,
  MonthlySummaryListQuery,
  MonthlySummaryRow,
  UploadMonthlyDocumentsResult,
} from '@/types/logsheet-monthly';
import type { PaginatedResult } from '@/types/roles';

// ---------------------------------------------------------------------------
// Request functions
// ---------------------------------------------------------------------------

/**
 * GET /api/logsheets/monthly-summary — paginated per-machine monthly summary.
 * Permission: logsheet.monthly.read
 */
export async function getMonthlySummaryRequest(
  query: MonthlySummaryListQuery,
): Promise<PaginatedResult<MonthlySummaryRow>> {
  try {
    const { data } = await api.get<PaginatedResult<MonthlySummaryRow>>(
      '/api/logsheets/monthly-summary',
      {
        params: {
          month: query.month,
          ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
          ...(query.workOrderId !== undefined ? { workOrderId: query.workOrderId } : {}),
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 20,
        },
      },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/monthly-summary/entries/{machineId}?month=YYYY-MM
 * Drill-through: all entries for a (machineId, month) tuple.
 * Permission: logsheet.monthly.read
 */
export async function getEntriesForMachineMonthRequest(
  machineId: number,
  month: string,
): Promise<readonly MonthlyEntryItem[]> {
  try {
    const { data } = await api.get<readonly MonthlyEntryItem[]>(
      `/api/logsheets/monthly-summary/entries/${String(machineId)}`,
      { params: { month } },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/monthly-summary/eligible?siteId=…
 * Lists (machineId, month) tuples eligible for monthly-document upload.
 * Permission: logsheet.monthly.read
 */
export async function getEligibleForMonthlyDocumentRequest(
  siteId?: number,
): Promise<readonly EligibleMonthlyTuple[]> {
  try {
    const { data } = await api.get<readonly EligibleMonthlyTuple[]>(
      '/api/logsheets/monthly-summary/eligible',
      {
        params: siteId !== undefined ? { siteId } : {},
      },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

interface UploadMonthlyDocumentsParams {
  readonly machineId: number;
  readonly month: string;
  readonly combinedDailyLogsheet: File;
  readonly monthlySummaryLogsheet: File;
}

/**
 * POST /api/logsheets/monthly-documents — multipart upload of 2 PDFs; locks the month.
 * Permission: logsheet.monthly.upload-documents
 * Error codes:
 *   409 LOGSHEET_NOT_ALL_APPROVED    — some entries for the month are not yet Approved
 *   409 LOGSHEET_MONTH_ALREADY_LOCKED — this month is already locked
 *   423 LOGSHEET_LOCKED_MONTH         — (shouldn't occur here but guard anyway)
 */
/**
 * REVIEW FF-012: accept an optional `AbortSignal` so the dialog can cancel
 * an in-flight multipart upload when the user dismisses the modal mid-upload
 * (previously the network call ran to completion even though the user
 * clearly intended to cancel — and the backend would still lock the month).
 */
export async function uploadMonthlyDocumentsRequest(
  params: UploadMonthlyDocumentsParams,
  options: { readonly signal?: AbortSignal } = {},
): Promise<UploadMonthlyDocumentsResult> {
  try {
    const form = new FormData();
    form.append('machineId', String(params.machineId));
    form.append('month', params.month);
    form.append('combinedDailyLogsheet', params.combinedDailyLogsheet);
    form.append('monthlySummaryLogsheet', params.monthlySummaryLogsheet);

    // Clear Content-Type header so axios sets `multipart/form-data; boundary=…` automatically.
    // We delete the header from the FormData request rather than setting it to undefined,
    // since exactOptionalPropertyTypes disallows undefined values in the header object.
    const { data } = await api.post<UploadMonthlyDocumentsResult>(
      '/api/logsheets/monthly-documents',
      form,
      options.signal ? { signal: options.signal } : undefined,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/monthly-summary/export.csv — CSV stream.
 * Permission: logsheet.monthly.export
 * Returns a Blob; caller triggers browser download.
 */
export async function getMonthlySummaryCsvRequest(
  query: MonthlySummaryListQuery,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>('/api/logsheets/monthly-summary/export.csv', {
      responseType: 'blob',
      params: {
        month: query.month,
        ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
        ...(query.workOrderId !== undefined ? { workOrderId: query.workOrderId } : {}),
      },
    });
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/monthly-summary/export.pdf — PDF stream.
 * Permission: logsheet.monthly.export
 * Returns a Blob; caller triggers browser download.
 */
export async function getMonthlySummaryPdfRequest(
  query: MonthlySummaryListQuery,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>('/api/logsheets/monthly-summary/export.pdf', {
      responseType: 'blob',
      params: {
        month: query.month,
        ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
        ...(query.workOrderId !== undefined ? { workOrderId: query.workOrderId } : {}),
      },
    });
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

// ---------------------------------------------------------------------------
// Browser blob download helpers (T-15-26: revokeObjectURL after anchor click)
// ---------------------------------------------------------------------------

/**
 * Triggers a browser file download using a synthetic anchor click.
 * Calls URL.revokeObjectURL after click to prevent blob URL leak (T-15-26).
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download monthly summary as CSV file.
 * Fetches the blob then triggers a synthetic anchor click.
 * Does NOT use TanStack Query (one-shot imperative action, not reactive state).
 */
export async function downloadMonthlySummaryCsv(query: MonthlySummaryListQuery): Promise<void> {
  const blob = await getMonthlySummaryCsvRequest(query);
  triggerBlobDownload(blob, `monthly-summary-${query.month}.csv`);
}

/**
 * Download monthly summary as PDF file.
 * Fetches the blob then triggers a synthetic anchor click.
 * Does NOT use TanStack Query (one-shot imperative action, not reactive state).
 */
export async function downloadMonthlySummaryPdf(query: MonthlySummaryListQuery): Promise<void> {
  const blob = await getMonthlySummaryPdfRequest(query);
  triggerBlobDownload(blob, `monthly-summary-${query.month}.pdf`);
}
